import PocketBase from "pocketbase";

/**
 * Single PocketBase client for the SPA.
 *
 * Safe as a module singleton in the browser because each browser tab is its
 * own JS realm with one authenticated user. The race condition that exists
 * for a *server-side* shared PB client does not apply here.
 *
 * Base URL is "/" so the SDK resolves API paths root-relative — not relative
 * to the current page path. Empty string would let the SDK splice the current
 * pathname in front of /api/..., breaking auth on any non-root route.
 *
 * In production (single Go binary) the SPA and PB API share the same origin,
 * so root-relative just works. In dev, Vite proxies `/api`, `/custom`, `/_`
 * to `http://localhost:8090`. Override via `VITE_PB_URL` if you ever need to
 * point the SPA at a different host.
 */
export const pb = new PocketBase(import.meta.env.VITE_PB_URL ?? "/");

pb.autoCancellation(false);

export function isAuthed(): boolean {
  return pb.authStore.isValid;
}

export function currentRole(): string | undefined {
  const model = pb.authStore.model as { role?: string } | null;
  return model?.role;
}

/**
 * Student viewers have their batch denormalized onto auth — return it so
 * list queries can narrow with `batch.id = '<id>'` instead of relying on
 * PB's per-row API rule evaluation.
 */
export function currentStudentBatchId(): string | undefined {
  const model = pb.authStore.model as { role?: string; batch_id?: string } | null;
  if (model?.role !== "student") return undefined;
  return model.batch_id || undefined;
}

/**
 * A student viewer's own `student` record id, denormalized onto auth as
 * `student_id`. This is the STUDENT record id (not the auth/login id) — the
 * value used by `/students/$id` params and by RCC's `student_id` column.
 * Route guards compare it against the requested id to keep students on their
 * own pages. Empty for teacher/support.
 */
export function currentStudentId(): string | undefined {
  const model = pb.authStore.model as { role?: string; student_id?: string } | null;
  if (model?.role !== "student") return undefined;
  return model.student_id || undefined;
}

export async function signOut(): Promise<void> {
  pb.authStore.clear();
}
